import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Button,
  FieldGroup,
  FieldRow,
  FieldSet,
  Input,
  SelectBox,
  TextArea,
} from "react-corner-ds";
import { Controller, FieldError, useForm } from "react-hook-form";
import { Trans, useTranslation } from "react-i18next";
import { definePatternByName } from "../../utils/constants";
import { ContactB2BFormInfo } from "../../models/request/data/data.model";
import { CountryItem, DataAttribute } from "../../models/utils.model";
import { cantonItems } from "../../assets/locales/utils";
import { countriesDe } from "../../assets/countries/de";
import { countriesEn } from "../../assets/countries/en";
import { countriesFr } from "../../assets/countries/fr";
import { countriesIt } from "../../assets/countries/it";
import { countriesCom } from "../../assets/countries/com";
import { Request } from "../../models/request/request.model";
import { useFlow } from "../../hooks/useFlow/useFlow";
import style from "./CtWidgetContactB2B.module.css";
import CtWidgetContactB2BSuccess from "../successComponents/ctWidgetContactB2B/CtWidgetContactB2BSuccess";
import GooglCaptchaDisclaimer from "../googleCaptchaDisclaimer/googleCaptchaDisclaimer";
import { EmailValidator } from "commons-validator-js";
import { parsePhoneNumber, phoneIsValid } from "../../utils/reformat";

interface ICtWidgetContactB2B {
  dataAttribute: DataAttribute;
}

const CtWidgetContactB2B: React.FC<ICtWidgetContactB2B> = ({
  dataAttribute,
}) => {
  const { t, i18n } = useTranslation();

  const [loading, setLoading] = useState<boolean>(false);
  const [isChCountry, setIsChCountry] = useState<boolean>(false);
  const [stepForm, setStepForm] = useState<string>("contactB2BForm");
  const [isError, setIsError] = useState<boolean>(false);
  const [isChSite, setIsChSite] = useState<boolean>(false);
  const [country, setCountry] = useState("CH");

  const { callSubscribe } = useFlow();

  const selectedLang = useMemo(
    () => i18n.resolvedLanguage,
    [i18n.resolvedLanguage]
  );

  // set trk Adobe Analytics
  const buttonSubmit = document.getElementsByClassName("trkContactB2B");
  buttonSubmit[0]?.setAttribute("data-trk-btn-click", "submit-contactb2b");

  const countries = useMemo(() => {
    switch (selectedLang) {
      case "de":
        return countriesDe;

      case "fr":
        return countriesFr;

      case "it":
        return countriesIt;

      default:
        return isChSite ? countriesEn : countriesCom;
    }
  }, [isChSite, selectedLang]);

  const validity = useCallback(
    (error: FieldError | undefined, isTouched: boolean) =>
      !error && isTouched ? true : undefined,
    []
  );

  const countryItems = useMemo(() => {
    return countries.map((item: CountryItem) => ({
      label: [item.name],
      value: item.alpha_2,
      icon: item.alpha_2,
    }));
  }, [countries]);

  const prefixItems = useMemo(() => {
    return countries.map((item: CountryItem) => ({
      label: [item.name, "+" + item.phone_code],
      value: "+" + item.phone_code,
      icon: item.alpha_2,
    }));
  }, [countries]);

  const { handleSubmit, formState, control, setValue, getValues, trigger } =
    useForm<ContactB2BFormInfo>({
      mode: "all",
      reValidateMode: "onChange",
      defaultValues: {
        FirstName: "",
        LastName: "",
        Company: "",
        Position: "",
        Email: "",
        Phones: [
          {
            CountryCode: "",
            LocalNumber: "",
            Type: "Mobile",
          },
        ],
        Addresses: [
          {
            Type: "Home",
            Country: "",
            Canton: "",
          },
        ],
        Notes: "",
      },
    });

  useEffect(() => {
    const hostName = window.location.host;
    if (hostName.includes("cornertrader.ch")) {
      setIsChSite(true);
      setValue("Phones.0.CountryCode", "+41");
      setCountry("CH");
    } else {
      setIsChSite(false);
      setValue("Phones.0.CountryCode", "+1242");
      setCountry("BS");
    }
  }, [setValue]);

  useEffect(() => {
    trigger("Addresses.0.Canton");
  }, [isChCountry]);

  const submitHandler = async (data: ContactB2BFormInfo) => {
    let currentDate = new Date();
    let currentYear = currentDate.getFullYear();
    let currentMonth =
      currentDate.getMonth() + 1 < 10
        ? "0" + (currentDate.getMonth() + 1)
        : currentDate.getMonth() + 1;
    let currentDay =
      currentDate.getDate() < 10
        ? "0" + currentDate.getDate()
        : currentDate.getDate();

    let body: Request = {
      Data: {
        CID: dataAttribute.dataWidgetCid,
        Promotion: dataAttribute.dataWidgetPromo,
        Notes: data.Notes,
        Trader: {
          Account: {
            TCAccepted: true,
            TCDate: `${currentYear}${currentMonth}${currentDay}`,
            TCCode: "1",
          },
          Customer: {
            Language: i18n.resolvedLanguage || "en",
            Email: data.Email,
            FirstName: data.FirstName,
            LastName: data.LastName,
            FinancialInformation: {
              Position: data.Position,
              Employer: {
                FirstName: data.Company,
              },
            },
            Phones: [
              {
                CountryCode: data.Phones[0].CountryCode,
                LocalNumber: data.Phones[0].LocalNumber?.replaceAll(" ", ""),
                Type: "Mobile",
              },
            ],
            Addresses: [
              {
                Canton: data.Addresses[0].Canton,
                Country: data.Addresses[0].Country,
                Type: "Home",
              },
            ],
          },
        },
      },
      Extra: {
        caseType: "ContactB2B",
      },
    };

    setLoading(true);
    let resp = await callSubscribe(body);

    if (resp?.data.status.success === true) {
      setStepForm(resp.data.status.step || "contactB2BForm");
    } else {
      setIsError(true);
    }
    setLoading(false);
  };

  const firstNameInput = useMemo(
    () => (
      <Controller
        control={control}
        name="FirstName"
        defaultValue={""}
        rules={{
          required: {
            value: true,
            message: t`errors.input.required`,
          },
          pattern: {
            value: definePatternByName("FirstName"),
            message: t`errors.input.invalidPattern`,
          },
        }}
        render={({
          field: { onChange, onBlur, name, value },
          fieldState: { isTouched, error },
        }) => (
          <>
            <label className={style.customLabel}>
              {t`screens.contactB2B.firstName` || ""}
            </label>
            <Input
              name={name}
              onChange={(e) => {
                onChange(e.target.value);
              }}
              onBlur={onBlur}
              valid={error ? false : validity(error, isTouched)}
              errorMsg={error?.message}
              maxLength={60}
              value={value}
              customCssClass={style.customInput}
            />
          </>
        )}
      />
    ),
    [control, t, validity]
  );

  const lastNameInput = useMemo(
    () => (
      <Controller
        control={control}
        name="LastName"
        defaultValue={""}
        rules={{
          required: {
            value: true,
            message: t`errors.input.required`,
          },
          pattern: {
            value: definePatternByName("LastName"),
            message: t`errors.input.invalidPattern`,
          },
        }}
        render={({
          field: { onChange, onBlur, name, value },
          fieldState: { isTouched, error },
        }) => (
          <>
            <label className={style.customLabel}>
              {t`screens.contactB2B.lastName` || ""}
            </label>
            <Input
              name={name}
              onChange={(e) => {
                onChange(e.target.value);
              }}
              onBlur={onBlur}
              valid={error ? false : validity(error, isTouched)}
              errorMsg={error?.message}
              maxLength={60}
              value={value}
              customCssClass={style.customInput}
            />
          </>
        )}
      />
    ),
    [control, t, validity]
  );

  const companyInput = useMemo(
    () => (
      <Controller
        control={control}
        name="Company"
        defaultValue={""}
        rules={{
          required: {
            value: true,
            message: t`errors.input.required`,
          },
          pattern: {
            value: definePatternByName("Text"),
            message: t`errors.input.invalidPattern`,
          },
        }}
        render={({
          field: { onChange, onBlur, name, value },
          fieldState: { isTouched, error },
        }) => (
          <>
            <label className={style.customLabel}>
              {t`screens.contactB2B.company` || ""}
            </label>
            <Input
              name={name}
              onChange={(e) => {
                onChange(e.target.value);
              }}
              onBlur={onBlur}
              valid={error ? false : validity(error, isTouched)}
              errorMsg={error?.message}
              maxLength={60}
              value={value}
              customCssClass={style.customInput}
            />
          </>
        )}
      />
    ),
    [control, t, validity]
  );

  const positionInput = useMemo(
    () => (
      <Controller
        control={control}
        name="Position"
        defaultValue={""}
        rules={{
          required: {
            value: true,
            message: t`errors.input.required`,
          },
          pattern: {
            value: definePatternByName("Text"),
            message: t`errors.input.invalidPattern`,
          },
        }}
        render={({
          field: { onChange, onBlur, name, value },
          fieldState: { isTouched, error },
        }) => (
          <>
            <label className={style.customLabel}>
              {t`screens.contactB2B.position` || ""}
            </label>
            <Input
              name={name}
              onChange={(e) => {
                onChange(e.target.value);
              }}
              onBlur={onBlur}
              valid={error ? false : validity(error, isTouched)}
              errorMsg={error?.message}
              maxLength={60}
              value={value}
              customCssClass={style.customInput}
            />
          </>
        )}
      />
    ),
    [control, t, validity]
  );

  const validator = useMemo(() => new EmailValidator(), []);

  const emailInput = useMemo(
    () => (
      <Controller
        control={control}
        name="Email"
        defaultValue={""}
        rules={{
          required: {
            value: true,
            message: t`errors.input.required`,
          },
          validate: (value) => validator.isValid(value),
        }}
        render={({
          field: { onChange, onBlur, name, value },
          fieldState: { isTouched, error },
        }) => (
          <>
            <label className={style.customLabel}>
              {t`screens.contactB2B.email` || ""}
            </label>
            <Input
              name={name}
              onChange={(e) => {
                onChange(e.target.value);
              }}
              type="email"
              onBlur={onBlur}
              valid={error ? false : validity(error, isTouched)}
              errorMsg={
                error?.message ||
                (!validator.isValid(value) &&
                  t`screens.contactB2B.emailRequired`) ||
                ""
              }
              maxLength={60}
              value={value}
              customCssClass={style.customInput}
            />
          </>
        )}
      />
    ),
    [control, t, validator, validity]
  );

  const prefixSelectBox = useMemo(
    () => (
      <Controller
        control={control}
        name="Phones.0.CountryCode"
        defaultValue={"+41"}
        render={({ field: { onChange, name, value } }) => (
          <SelectBox
            cssClass="prefix"
            value={value}
            previewIndex="1"
            items={prefixItems}
            name={name}
            onChange={(item: any) => {
              onChange(item.value);
              setCountry(item.icon);
              setValue("Phones.0.LocalNumber", "");
            }}
          />
        )}
      />
    ),
    [control, prefixItems, setValue]
  );

  const phoneInput = useMemo(
    () => (
      <Controller
        control={control}
        name="Phones.0.LocalNumber"
        defaultValue={""}
        rules={{
          validate: (value) => phoneIsValid(value, country, t),

          required: {
            value: true,
            message: t`screens.contactB2B.phoneRequired`,
          },
          minLength: {
            value: 7,
            message: t(`errors.input.minPhoneLength`, {
              min: "7",
            }),
          },
        }}
        render={({
          field: { onChange, onBlur, name, value },
          fieldState: { isTouched, error },
        }) => (
          <>
            <label className={style.customLabel}>
              {t`screens.contactB2B.phoneNr` || ""}
            </label>
            <Input
              type="tel"
              name={name}
              customCssClass={style.customPhoneInput}
              cssClass="prefixed"
              onBlur={onBlur}
              onChange={(ev: any) => {
                onChange(
                  parsePhoneNumber(
                    getValues("Phones.0.CountryCode"),
                    ev.target.value
                  )
                );
              }}
              valid={error ? false : validity(error, isTouched)}
              errorMsg={error?.message}
              maxLength={13}
              value={value}
            >
              {prefixSelectBox}
            </Input>
          </>
        )}
      />
    ),
    [control, prefixSelectBox, t, validity, country, getValues]
  );

  const countrySelectBox = useMemo(
    () => (
      <Controller
        control={control}
        name="Addresses.0.Country"
        rules={{
          required: {
            value: true,
            message: t`errors.input.required`,
          },
        }}
        render={({
          field: { onChange, name, value, onBlur },
          fieldState: { error },
        }) => (
          <>
            <label className={style.customLabel}>
              {t`screens.contactB2B.country` || ""}
            </label>
            <SelectBox
              items={countryItems}
              name={name}
              value={value}
              onChange={(item) => {
                onChange(item.value);
                setIsChCountry(item.value === "CH");
              }}
              onBlur={onBlur}
              invalid={!!error?.message}
              errorMsg={error?.message}
              customCssClass={style.customInput}
            />
          </>
        )}
      />
    ),
    [control, countryItems, t]
  );

  const cantonSelectBox = useMemo(
    () => (
      <Controller
        control={control}
        name="Addresses.0.Canton"
        rules={{
          required: {
            value: true,
            message: t`errors.input.required`,
          },
        }}
        render={({
          field: { onChange, name, value, onBlur },
          fieldState: { error },
        }) => (
          <>
            <label className={style.customLabel}>
              {t`screens.contactB2B.canton` || ""}
            </label>
            <SelectBox
              items={cantonItems(t)}
              name={name}
              value={value}
              onChange={(item) => {
                onChange(item.value);
              }}
              onBlur={onBlur}
              invalid={!!error?.message}
              errorMsg={error?.message}
              customCssClass={style.customInput}
            />
          </>
        )}
      />
    ),
    [control, t]
  );

  const messageTextArea = useMemo(
    () => (
      <Controller
        control={control}
        name="Notes"
        defaultValue={""}
        rules={{
          required: {
            value: true,
            message: t`errors.input.required`,
          },
          pattern: {
            value: definePatternByName("Message"),
            message: t`errors.input.invalidPattern`,
          },
        }}
        render={({
          field: { onChange, onBlur, name, value },
          fieldState: { isTouched, error },
        }) => (
          <>
            <label className={style.customLabel}>
              {t`screens.contactB2B.notes` || ""}
            </label>
            <TextArea
              name={name}
              onChange={(e) => {
                onChange(e.target.value);
              }}
              onBlur={onBlur}
              valid={error ? false : validity(error, isTouched)}
              errorMsg={error?.message}
              maxLength={160}
              value={value}
              customCssClass={style.customInput}
            />
          </>
        )}
      />
    ),
    [control, t, validity]
  );

  const disclaimerInfo = useMemo(
    () => (
      <div className={style.disclaimerInfo}>
        <Trans
          i18nKey={
            isChSite
              ? t`screens.contactB2B.disclaimerCh`
              : t`screens.contactB2B.disclaimerCom`
          }
          components={{
            B: <ul />,
            S: <li />,
          }}
        />
      </div>
    ),
    [isChSite, t]
  );

  return stepForm === "FormSubmitted" ? (
    <CtWidgetContactB2BSuccess
      selectedLang={selectedLang}
      hideTitle={dataAttribute.dataWidgetHideTitle || false}
    />
  ) : (
    <div className={style.action}>
      <div className={style.contactB2BForm}>
        {!dataAttribute.dataWidgetHideTitle ? (
          <>
            <h1
              className={style.contactB2BTitle}
            >{t`screens.contactB2B.formTitle`}</h1>
            <h2
              className={style.contactB2BSubTitle}
            >{t`screens.contactB2B.formSubTitle`}</h2>
          </>
        ) : (
          <></>
        )}

        <form autoComplete="off" onSubmit={handleSubmit(submitHandler)}>
          <FieldSet>
            <FieldRow
              customCssClass={
                dataAttribute.halfContainer ? style.customFieldRow : ""
              }
            >
              <FieldGroup>{firstNameInput}</FieldGroup>
              <FieldGroup>{lastNameInput}</FieldGroup>
            </FieldRow>

            <FieldRow
              customCssClass={
                dataAttribute.halfContainer ? style.customFieldRow : ""
              }
            >
              <FieldGroup>{companyInput}</FieldGroup>
              <FieldGroup>{positionInput}</FieldGroup>
            </FieldRow>

            <FieldRow
              customCssClass={
                dataAttribute.halfContainer ? style.customFieldRow : ""
              }
            >
              <FieldGroup>{emailInput}</FieldGroup>
              <FieldGroup>{phoneInput}</FieldGroup>
            </FieldRow>

            <FieldRow
              customCssClass={
                dataAttribute.halfContainer ? style.customFieldRow : ""
              }
            >
              <FieldGroup>{countrySelectBox}</FieldGroup>
              {isChCountry ? (
                <FieldGroup>{cantonSelectBox}</FieldGroup>
              ) : (
                <FieldGroup cssClass="void"> </FieldGroup>
              )}
            </FieldRow>

            <FieldRow
              customCssClass={
                dataAttribute.halfContainer ? style.customFieldRow : ""
              }
            >
              <FieldGroup>{messageTextArea}</FieldGroup>
            </FieldRow>

            <FieldRow
              customCssClass={
                dataAttribute.halfContainer ? style.customFieldRow : ""
              }
            >
              <FieldGroup>{disclaimerInfo}</FieldGroup>
            </FieldRow>
          </FieldSet>
          <div className={style.cta}>
            <Button
              text={t`screens.contactB2B.submit`}
              disabled={!formState.isValid || loading}
              type="submit"
              icon={
                loading
                  ? { icon: "loader", position: "right", spin: true }
                  : undefined
              }
              // set trk Adobe Analytics
              customCssClass="trkContactB2B"
            />
          </div>
          {isError ? (
            <FieldRow>
              <FieldGroup customCssClass={style.containerError}>
                <span className={style.errorSubmit}>{t`errors.submit`}</span>
              </FieldGroup>
            </FieldRow>
          ) : (
            <></>
          )}
          <GooglCaptchaDisclaimer></GooglCaptchaDisclaimer>
        </form>
      </div>
    </div>
  );
};

export default CtWidgetContactB2B;
